import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Modal, Space, Table, Tabs, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useAssignClientAttachmentToContract,
  useAssignSignedDocumentToContract,
  useFilterDocumentsToSignForClient,
  useGetClientAttachments,
} from "../../../../../common/api/queries";
import { ModalSizes, PageSizes } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { ClientAttachmentType } from "../../../../client/enums";
import { CLIENT_ROUTE_PATHS } from "../../../../client/paths";
import { ClientAttachment } from "../../../../client/types";
import { DocSignatureStatus, DocSignatureView } from "../../../../docsignature/enums";
import { CLIENT_DOCUMENT_SIGN_PATHS } from "../../../../docsignature/paths";
import { DocSignature } from "../../../../docsignature/types";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  clientId: UUID;
  clientName: string;
  contractId: UUID;
};

export const ContractClientAttachmentsModal = ({ isOpen, onCancel, clientId, clientName, contractId }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [signedDocumentsPageIndex, setSignedDocumentsPageIndex] = useState(0);

  const { data: signedDocuments, isFetching: signedDocumentsFetching } = useFilterDocumentsToSignForClient(
    {
      id: clientId,
      object: {
        view: DocSignatureView.AGENT,
        pageSize: PageSizes.SMALL,
        pageIndex: signedDocumentsPageIndex,
        statuses: [DocSignatureStatus.SIGNED],
      },
    },
    isTopAgentPlus && isOpen,
  );

  const { data: clientAttachments, isFetching: clientAttachmentsFetching } = useGetClientAttachments(clientId, isOpen);

  const assignClientAttachmentToContract = useAssignClientAttachmentToContract();
  const assignSignedDocumentToContract = useAssignSignedDocumentToContract();

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={isOpen}
      title={`${t("contract.titles.assignAttachmentsFromClient")} - ${clientName}`}
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>{t("common.close")}</Button>}
      mask={{ closable: false }}
    >
      <Tabs
        items={[
          isTopAgentPlus
            ? {
                key: "signedDocuments",
                label: t("contract.helpers.signedDocuments"),
                children: (
                  <Table<DocSignature>
                    {...tableStandardProps()}
                    scroll={{ x: 800 }}
                    loading={signedDocumentsFetching}
                    columns={[
                      {
                        key: "createdAt",
                        title: t("documentSign.attrs.createdAt"),
                        width: 160,
                        render: (_, record) => formatLocaleDateTime(record.createdAt),
                      },
                      {
                        key: "filename",
                        title: t("documentSign.helpers.filename"),
                        width: 120,
                        ellipsis: { showTitle: false },
                        render: (_, record) => <Ellipsis>{record.originalFile.filename}</Ellipsis>,
                      },
                      {
                        key: "type",
                        title: t("documentSign.enums.docSignatureType._label"),
                        width: 130,
                        render: (_, record) => t(`documentSign.enums.docSignatureType.${record.type}`),
                      },
                      {
                        key: "signedAt",
                        title: t("documentSign.attrs.signedAt"),
                        width: 160,
                        render: (_, record) => (record.signedAt ? formatLocaleDateTime(record.signedAt) : undefined),
                      },
                      {
                        key: "actions",
                        align: "right",
                        width: 230,
                        fixed: "right",
                        render: (_, record) => (
                          <Space>
                            <ActionTextIcon
                              path={generatePath(CLIENT_DOCUMENT_SIGN_PATHS.signedFile.to, {
                                id1: clientId,
                                id2: record.id,
                              })}
                              color="blue"
                              icon="export"
                              text={t("common.open")}
                              target="_blank"
                            />

                            <ActionTextIcon
                              icon="link"
                              color="green"
                              text={t("contract.actions.assignToContract")}
                              onClick={() =>
                                assignSignedDocumentToContract.mutate({
                                  id1: clientId,
                                  id2: record.id,
                                  id3: contractId,
                                })
                              }
                              disabled={assignSignedDocumentToContract.isPending}
                            />
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={signedDocuments?.pageData}
                    pagination={{
                      ...paginationTableProps,
                      placement: ["bottomEnd"],
                      current: numberOrZero(signedDocuments?.pageIndex) + 1,
                      pageSize: signedDocuments?.pageSize,
                      total: signedDocuments?.totalElementsCount,
                      onChange: (pageIndex) => setSignedDocumentsPageIndex(pageIndex - 1),
                    }}
                  />
                ),
              }
            : undefined,
          {
            key: "otherDocuments",
            label: t("contract.helpers.otherDocuments"),
            children: (
              <Table<ClientAttachment>
                {...tableStandardProps()}
                columns={[
                  {
                    key: "createdAt",
                    title: t("common.createdAt"),
                    width: 160,
                    render: (_, record) => (record.createdAt ? formatLocaleDateTime(record.createdAt) : undefined),
                  },
                  {
                    key: "type",
                    width: 50,
                    align: "center",
                    render: (_, record) => {
                      if (record.type === ClientAttachmentType.BACK_ID_CARD) {
                        return (
                          <Tooltip title={t("client.helpers.backSideIdentityCard")}>
                            <span>
                              <AntIcon type="pic-right" />
                            </span>
                          </Tooltip>
                        );
                      } else if (record.type === ClientAttachmentType.FRONT_ID_CARD) {
                        return (
                          <Tooltip title={t("client.helpers.frontSideIdentityCard")}>
                            <span>
                              <AntIcon type="pic-left" />
                            </span>
                          </Tooltip>
                        );
                      }

                      return;
                    },
                  },
                  {
                    key: "filename",
                    title: t("common.filename"),
                    ellipsis: { showTitle: false },
                    width: 210,
                    render: (_, record) => (
                      <FileTypeIconWithFilename
                        contentType={record.file.contentType}
                        filename={record.file.filename}
                        ellipsis
                      />
                    ),
                  },
                  {
                    key: "size",
                    title: t("common.filesize"),
                    width: 150,
                    render: (_, record) => formatFileSize(record.file.size),
                  },
                  {
                    key: "actions",
                    align: "right",
                    width: 230,
                    fixed: "right",
                    render: (_, record) => (
                      <Space>
                        <ActionTextIcon
                          path={generatePath(CLIENT_ROUTE_PATHS.attachment.to, { id1: clientId, id2: record.id })}
                          target="_blank"
                          icon="export"
                          color="blue"
                          text={t("common.open")}
                        />

                        <ActionTextIcon
                          icon="link"
                          color="green"
                          text={t("contract.actions.assignToContract")}
                          onClick={() =>
                            assignClientAttachmentToContract.mutate({ id1: clientId, id2: record.id, id3: contractId })
                          }
                          disabled={assignClientAttachmentToContract.isPending}
                        />
                      </Space>
                    ),
                  },
                ]}
                loading={clientAttachmentsFetching}
                scroll={{ x: 800 }}
                dataSource={clientAttachments}
                pagination={{
                  ...paginationTableProps,
                  placement: ["bottomEnd"],
                  pageSize: PageSizes.LARGE,
                  total: clientAttachments?.length,
                }}
              />
            ),
          },
        ].filter((item) => !!item)}
      />
    </Modal>
  );
};
